import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFetcher } from 'react-router-dom';
import { Temporal } from 'temporal-polyfill';
import { TemporalFormatter } from '@/components/TemporalFormatter';
import { TimeFormatter } from '@/components/TimeFormatter.tsx';
import { Button } from '@/components/ui/button.tsx';
import { useClock } from '@/contexts/ClockContext';
import { useLocale } from '@/contexts/LocaleContext.tsx';
import type { LeadScheduledDate } from '@/models/entities/lead';
import Actions from '@/models/ui/actions.ts';
import { CalendarModel } from '@/models/ui/calendar.ts';

interface CalendarProps {
  readonly isReadOnly: boolean;
  readonly scheduledDate?: LeadScheduledDate | null;
}

export function Calendar({ isReadOnly, scheduledDate }: CalendarProps) {
  const { clock } = useClock();
  const timeZone = clock.getTimeZone();
  const { locale } = useLocale();
  const calendarModel = useMemo(() => new CalendarModel(locale), [locale]);
  const fetcher = useFetcher({ key: 'workflow-fetcher' });

  const [selection, setSelection] = useState<LeadScheduledDate | null>(() => {
    if (scheduledDate)
      return scheduledDate
        .toInstant()
        .toZonedDateTimeISO(timeZone) as LeadScheduledDate;
    return null;
  });

  const [currentYearMonth, setCurrentYearMonth] = useState(() => {
    if (selection) return Temporal.PlainYearMonth.from(selection);
    return Temporal.PlainYearMonth.from(clock.getZonedDateTimeISO());
  });

  const selectedDateStr = selection?.toPlainDate().toString();
  const selectedTimeStr = selection?.toPlainTime().toString();

  const daysOfWeek = useMemo(() => calendarModel.daysOfWeek(), [calendarModel]);
  const monthNames = useMemo(
    () => calendarModel.getMonthNames(),
    [calendarModel],
  );
  const weeks = useMemo(
    () => calendarModel.getCalendarWeeks(currentYearMonth),
    [calendarModel, currentYearMonth],
  );
  const timeSlots = useMemo(
    () => calendarModel.getTimeSlots(),
    [calendarModel],
  );

  const handlePrevMonth = () =>
    setCurrentYearMonth((prev) => prev.subtract({ months: 1 }));
  const handleNextMonth = () =>
    setCurrentYearMonth((prev) => prev.add({ months: 1 }));

  const handleDateChange = (dateStr: string) => {
    const newDate = Temporal.PlainDate.from(dateStr);
    setSelection((prev) => {
      const time = prev?.toPlainTime() ?? Temporal.PlainTime.from('10:00:00');
      return newDate
        .toPlainDateTime(time)
        .toZonedDateTime(timeZone) as LeadScheduledDate;
    });
  };

  const handleTimeChange = (timeStr: string) => {
    const newTime = Temporal.PlainTime.from(timeStr);
    setSelection((prev) => {
      const date =
        prev?.toPlainDate() ?? clock.getZonedDateTimeISO().toPlainDate();
      return date
        .toPlainDateTime(newTime)
        .toZonedDateTime(timeZone) as LeadScheduledDate;
    });
  };

  return (
    <section className="calendar" aria-labelledby="calendar-title">
      <fetcher.Form method="post" id={Actions.ScheduleAppointment.form}>
        <h3 id="calendar-title" className="calendar-title">
          {isReadOnly ? 'SCHEDULED DATE & TIME' : 'SELECT DATE & TIME'}
        </h3>
        <div className="flex gap-12">
          <fieldset aria-label="Calendar" className="flex-1">
            <legend className="sr-only">Calendar</legend>
            <div className="calendar-header">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="p-1 h-auto w-auto"
                onClick={handlePrevMonth}
                aria-label="Previous month"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </Button>
              <span className="month-name">
                {monthNames[currentYearMonth.month - 1].toUpperCase()}{' '}
                {currentYearMonth.year}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="p-1 h-auto w-auto"
                onClick={handleNextMonth}
                aria-label="Next month"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </Button>
            </div>

            <table className="w-full border-separate border-spacing-1">
              <thead>
                <tr>
                  {daysOfWeek.map((day) => (
                    <th key={day} scope="col" className="weekday">
                      {day.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((week, weekIndex) => (
                  <tr key={`${currentYearMonth.month}-${weekIndex}`}>
                    {week.map((date) => {
                      const dateStr = date.toString();
                      return (
                        <td key={dateStr} className="p-0">
                          <label
                            aria-label={`Select ${dateStr} for scheduling`}
                            htmlFor={`date-${dateStr}`}
                            className={`day-label ${date.month !== currentYearMonth.month || date.year !== currentYearMonth.year ? 'other-month' : ''}`}
                          >
                            <input
                              id={`date-${dateStr}`}
                              type="radio"
                              name="calendar-date"
                              className="calendar-radio"
                              checked={selectedDateStr === dateStr}
                              value={dateStr}
                              disabled={
                                date.month !== currentYearMonth.month ||
                                date.year !== currentYearMonth.year ||
                                isReadOnly
                              }
                              onChange={(e) => handleDateChange(e.target.value)}
                            />
                            <span className="day-box">
                              <time dateTime={dateStr}>{date.day}</time>
                            </span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </fieldset>

          <fieldset className="flex-1 flex flex-col">
            <legend className="sr-only">Time Slots</legend>
            <ul className="grid grid-cols-2 gap-2 list-none p-0">
              {timeSlots.map((time) => {
                const timeStr = time.toString();
                return (
                  <li key={timeStr}>
                    <label htmlFor={`time-${timeStr}`} className="time-label">
                      <input
                        id={`time-${timeStr}`}
                        type="radio"
                        name="time-slot"
                        className="calendar-radio"
                        checked={selectedTimeStr === timeStr}
                        value={timeStr}
                        disabled={isReadOnly}
                        onChange={(e) => handleTimeChange(e.target.value)}
                      />
                      <span className="time-box">
                        <TimeFormatter value={time} />
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>

            <div
              className="selection-preview"
              aria-live="polite"
              aria-atomic="true"
            >
              {selection && (
                <div className="flex items-center justify-between">
                  <TemporalFormatter
                    className="text-sm font-bold"
                    value={selection}
                  />
                </div>
              )}
            </div>

            <div className="mt-auto flex justify-end h-8">
              {!isReadOnly && scheduledDate && (
                <Button
                  variant="outline"
                  type="submit"
                  name="action"
                  value={Actions.Reschedule.key}
                  size="sm"
                >
                  {Actions.Reschedule.label}
                </Button>
              )}
            </div>
          </fieldset>
        </div>

        <input
          type="hidden"
          name="scheduledDate"
          value={selection?.toInstant().toString() ?? ''}
        />
      </fetcher.Form>
    </section>
  );
}
